/**
 * Types + fetchery dla pluginu `c_shop` rozszerzajacego shell `c_user_accounts`
 * (zamowienia zalogowanego usera - lista + podglad + pobranie faktury PDF).
 *
 * Backend (IRESTAPI, routing po NAZWIE METODY - `IAPI_` strip):
 *   - GET  /IRESTAPI/v1/C_SHOP_CONTROLLER/userOrders     (12 mc + truncated)
 *   - GET  /IRESTAPI/v1/C_SHOP_CONTROLLER/userOrder/{id} (podglad zamowienia)
 *
 * PDF faktury pobierany jest bezposrednio z backendu przez zaszyfrowany link
 * `fvat_pdf_href` (`/IGETFILE/{blob}`) generowany po stronie serwera przez
 * `C_SHOP_CONTROLLER::getUserOrderFvatPdfUrl`. Wzorzec spojny z c_facture:
 * id zamowienia w tamper-proof payloadzie, ownership sprawdzany server-side
 * w handlerze `userOrderFvatPdfFile`. Browser nawiguje bezposrednio (bez proxy
 * z Bearer - IGETFILE dziala na PHP session cookie).
 *
 * Platnosc online: `payment_href` na liscie / szczegolach zamowienia to URL
 * ekranu platnosci centralnej bramki (`/IPAYMENT/{blob}?t=SCREEN&m=pay&token=...`)
 * generowany server-side przez `IPAYMENT_GATEWAY::createRequest` (konsument
 * `c_shop`). Browser nawiguje bezposrednio - ekran wyboru operatora +
 * przekierowanie + weryfikacja + dispatch wyniku do `C_SHOP_CONTROLLER::onPaymentResult`
 * dziejа sie poza React (parytet z m_facture flow).
 */

/**
 * Klucze statusu platnosci - parytet z LANG `c_user_account_payment_statuses`.
 * `set_as_paid` = admin ustawil recznie, `paid` = zaplacono online, `no_paid` = niezaplacone.
 */
export type OrderPaymentStatus = "set_as_paid" | "paid" | "no_paid";

/**
 * Stan przycisku faktury:
 *  - `none`          - user nie zaznaczyl faktury przy zamawianiu / modul niedostepny
 *  - `not_generated` - user zaznaczyl fakture ale system jeszcze jej nie wystawil
 *  - `not_paid`      - faktura wystawiona ale zamowienie nieoplacone (blokada pobrania)
 *  - `ready`         - PDF gotowy do pobrania (`fvat_pdf_href` niepuste)
 */
export type OrderFvatState = "none" | "not_generated" | "not_paid" | "ready";

export type UserOrder = {
  id: number;
  /** Numer zamowienia. */
  nr: string;
  /** Data zlozenia (Y-m-d H:i:s). */
  add_date: string;
  /** Id statusu z m_shop_orders.id_status (null gdy `default_status`). */
  id_status: number | null;
  /** Kwota brutto sformatowana przez backend (IFINANCE::getFormatedPrice). */
  order_total_price_brutto: string;
  /** Symbol waluty ISO (`PLN`, `EUR`, ...). */
  order_currency: string;
  /** Procent rabatu (null gdy brak). */
  discount: number | null;
  /** Kod rabatowy (null gdy brak / modul discount_codes wylaczony). */
  discount_code: string | null;
  /** Wartosc kolumny m_shop_orders.set_paid (0/1/2). */
  set_paid: number;
  payment_status: OrderPaymentStatus;
  is_paid: boolean;
  /** Czy user zaznaczyl fakture przy zamawianiu. */
  fvat_requested: boolean;
  fvat_state: OrderFvatState;
  /** Zaszyfrowany `/IGETFILE/{blob}` do PDF faktury (null poza `fvat_state=ready`). */
  fvat_pdf_href: string | null;
  /**
   * URL ekranu platnosci centralnej bramki (`/IPAYMENT/...token=...`) - null gdy
   * zamowienie oplacone. Ten sam wzorzec co `M_FACTURE_MCONTROLLER::getPaymentUrl`.
   */
  payment_href: string | null;
};

export type UserOrderListResponse = {
  ok: true;
  data: {
    orders: UserOrder[];
    /** `true` gdy lista zostala obcieta do limitu serwera (500). */
    truncated: boolean;
    facture_module_available: boolean;
  };
};

export type OrderProductVariant = {
  feature_name: string;
  feature_value_name: string;
  /** Absolutny URL miniatury tekstury (null gdy brak). */
  feature_value_texture: string | null;
};

export type OrderProduct = {
  id: number;
  name: string;
  symbol: string;
  ean: string;
  picture_src: string | null;
  count: number;
  /** Stawka VAT w procentach (np. 23). */
  vat_tax_rate: number;
  /** Wartosc pozycji brutto sformatowana. */
  amount_brutto: string;
  variants: OrderProductVariant[];
};

export type OrderPaymentOption = {
  name: string;
  /** m_shop_payment_options.payment_type: 1=przelew, 2=online, 3=punkt sprzedazy. */
  type: number;
  info: string;
  additional_info: string;
};

export type OrderDeliveryOption = {
  name: string;
  /** m_shop_delivery_options.type: 2 = InPost paczkomat. */
  type: number;
  price_brutto: string;
  free_delivery: boolean;
};

export type OrderInpostParcel = {
  name: string;
  address: string;
  location_description: string;
};

/** Snapshot punktu odbioru dla orderu z dostawą typu M_SHOP_DELIVERY_PICKUP_POINT.
    Odpowiednik OrderInpostParcel ale dla wlasnych punktow z m_shop_delivery_points. */
export type OrderPickupPoint = {
  name: string;
  /** Sklejony adres "ul. Szaniecka 7, 28-100 Busko-Zdroj" (backend juz zlepil). */
  address: string;
  /** Wspolrzedne do linku nawigacji Google Maps. NULL gdy brak w bazie. */
  latitude: number | null;
  longitude: number | null;
  /** Pelen URL miniaturki punktu. NULL gdy admin nie dodal obrazka. */
  image_url: string | null;
};

export type OrderDiscountCode = {
  code: string;
  percent: number;
};

export type UserOrderDetail = {
  id: number;
  nr: string;
  add_date: string;
  id_status: number | null;
  order_total_price_brutto: string;
  order_currency: string;
  set_paid: number;
  payment_status: OrderPaymentStatus;
  is_paid: boolean;
  fvat_requested: boolean;
  payment_href: string | null;
  totals: {
    products_count: number;
    products_total_brutto: string;
  };
  payment_option: OrderPaymentOption;
  delivery_option: OrderDeliveryOption;
  inpost_parcel: OrderInpostParcel | null;
  pickup_point: OrderPickupPoint | null;
  discount_code: OrderDiscountCode | null;
};

export type UserOrderDetailResponse = {
  ok: true;
  data: {
    order: UserOrderDetail;
    products: OrderProduct[];
  };
};

/**
 * Pobiera liste zamowien usera (12 miesiecy) z route handler proxy.
 *
 * @throws Error gdy fetch fail / response !ok (caller obsluguje przez try/catch).
 */
export async function fetchUserOrders(): Promise<{
  orders: UserOrder[];
  truncated: boolean;
  factureModuleAvailable: boolean;
}> {
  const res = await fetch("/api/iiapi/c-shop/orders", { cache: "no-store" });
  if (!res.ok) throw new Error(`Fetch orders failed: HTTP ${res.status}`);
  const body = (await res.json()) as
    | UserOrderListResponse
    | { ok: false; error?: string; message?: string };
  if (!("ok" in body) || body.ok !== true) {
    throw new Error("message" in body ? (body.message ?? "Fetch failed") : "Invalid response");
  }
  return {
    orders: body.data?.orders ?? [],
    truncated: body.data?.truncated ?? false,
    factureModuleAvailable: body.data?.facture_module_available ?? false,
  };
}

/**
 * Pobiera szczegoly pojedynczego zamowienia.
 *
 * @throws Error gdy zamowienie nie istnieje / nie nalezy do usera / fetch fail.
 */
export async function fetchUserOrder(orderId: number): Promise<{
  order: UserOrderDetail;
  products: OrderProduct[];
}> {
  const res = await fetch(`/api/iiapi/c-shop/order/${orderId}`, { cache: "no-store" });
  if (!res.ok) throw new Error(`Fetch order failed: HTTP ${res.status}`);
  const body = (await res.json()) as
    | UserOrderDetailResponse
    | { ok: false; error?: string; message?: string };
  if (!("ok" in body) || body.ok !== true) {
    throw new Error("message" in body ? (body.message ?? "Fetch failed") : "Invalid response");
  }
  return {
    order: body.data.order,
    products: body.data.products ?? [],
  };
}
